import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AreaChart, Area, ComposedChart, Bar, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { API, inputCls, Field, Modal, PrimaryBtn, errMsg } from '../organization/finance/shared';
import Icon from '../../components/dashboard/DashboardIcons';
import { relativeTime, dateOnly, PresenceDot } from './presence';
import { AdminWhatsAppModal } from './subscriptionWidgets';
import ClubProfileDrawer from './ClubProfileDrawer';

const sauth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const PLAN_LABELS = { free: 'مجانية', basic: 'أساسية', premium: 'مميزة', enterprise: 'مؤسسات', venue_trial: 'تجريبية (ملاعب)', coach_pro: 'المدرب الاحترافي' };
const planLabel = (p) => PLAN_LABELS[p] || p || 'غير محدد';
const ROLE_SHORT = { admin: 'مدير', manager: 'مشرف', coach: 'مدرب', analyst: 'محلل', parent: 'ولي أمر', venue_owner: 'مالك ملعب', super_admin: 'سوبر أدمن' };
const PLAN_COLORS = ['#0d9488', '#6366f1', '#f59e0b', '#ef4444', '#0ea5e9', '#a855f7'];
const nf = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('en-US'));
const money = (v) => (v === null || v === undefined ? '—' : Math.round(Number(v)).toLocaleString('en-US'));
const monthLabel = (m) => {
  const names = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
  const [, mm] = String(m).split('-');
  return names[Number(mm) - 1] || m;
};

const rise = (i = 0) => ({ animation: 'ccRise .45s cubic-bezier(.2,.7,.3,1) both', animationDelay: `${i * 45}ms` });

// شريحة اتجاه صغيرة: مقارنة بالشهر الماضي
const Trend = ({ value, suffix = '%', invert, dark }) => {
  if (value === null || value === undefined || !isFinite(value)) return null;
  const up = value > 0;
  const flat = value === 0;
  const good = invert ? !up : up;
  const cls = flat
    ? (dark ? 'bg-white/10 text-white/60' : 'bg-slate-100 text-slate-500')
    : good
      ? (dark ? 'bg-emerald-400/15 text-emerald-300' : 'bg-emerald-50 text-emerald-700')
      : (dark ? 'bg-red-400/15 text-red-300' : 'bg-red-50 text-red-600');
  return (
    <span className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums ${cls}`}>
      {flat ? '—' : up ? '▲' : '▼'} {flat ? '' : `${Math.abs(Math.round(value))}${suffix}`}
    </span>
  );
};

// ====================== بطاقة مؤشر ======================
const Kpi = ({ label, value, unit, sub, tone = 'light', accent, icon, live, onClick, testId, delay, trend, trendInvert }) => {
  const dark = tone === 'dark';
  const emerald = tone === 'emerald';
  const base = dark
    ? 'bg-[#0f1726] text-white border-white/10 shadow-lg shadow-slate-900/20'
    : emerald
      ? 'bg-[#06251c] text-emerald-50 border-emerald-800/40 shadow-lg shadow-emerald-950/20'
      : 'bg-white text-slate-900 border-slate-200/90 shadow-[0_1px_2px_rgba(15,23,42,.04)]';
  return (
    <button
      type="button" onClick={onClick} data-testid={testId} style={rise(delay)}
      className={`group relative flex flex-col items-start overflow-hidden rounded-2xl border p-4 text-right ${base} ${onClick ? 'cursor-pointer hover:-translate-y-0.5 hover:shadow-xl' : 'cursor-default'} transition-[transform,box-shadow] duration-200`}
    >
      <span className={`pointer-events-none absolute -left-10 -top-12 h-28 w-28 rounded-full blur-2xl ${dark ? 'bg-indigo-500/20' : emerald ? 'bg-emerald-400/20' : 'bg-teal-500/5'}`} />
      <div className="flex w-full items-center justify-between gap-2">
        <span className={`text-[11px] font-bold ${dark || emerald ? 'text-white/60' : 'text-slate-400'}`}>{label}</span>
        {live ? <PresenceDot online /> : icon ? <Icon name={icon} className={`h-4 w-4 ${dark || emerald ? 'text-white/40' : 'text-slate-300'}`} /> : null}
      </div>
      <div className="mt-2 flex items-baseline gap-1.5">
        <span className={`text-3xl font-extrabold tabular-nums leading-none ${accent || (dark || emerald ? 'text-white' : 'text-slate-900')}`}>{value}</span>
        {unit && <span className={`text-xs font-semibold ${dark || emerald ? 'text-white/50' : 'text-slate-400'}`}>{unit}</span>}
      </div>
      <span className={`mt-2 flex items-center gap-1.5 text-[11px] ${dark || emerald ? 'text-white/45' : 'text-slate-400'}`}>
        <Trend value={trend} invert={trendInvert} dark={dark || emerald} />
        {sub}
      </span>
    </button>
  );
};

const Panel = ({ title, hint, action, children, testId, delay, className = '' }) => (
  <section className={`overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_1px_2px_rgba(15,23,42,.04)] ${className}`} data-testid={testId} style={rise(delay)}>
    <header className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
      <div>
        <h3 className="text-sm font-bold text-slate-800">{title}</h3>
        {hint && <p className="mt-0.5 text-[11px] text-slate-400">{hint}</p>}
      </div>
      {action}
    </header>
    {children}
  </section>
);

const Empty = ({ text }) => <p className="px-4 py-8 text-center text-xs text-slate-400">{text}</p>;

const ChartTip = ({ active, payload, label, suffix }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-xl" dir="rtl">
      <div className="mb-1 text-[11px] font-bold text-slate-500">{monthLabel(label)}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 text-xs font-semibold text-slate-700">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
          {p.name}: <span className="tabular-nums">{nf(p.value)}</span> {suffix}
        </div>
      ))}
    </div>
  );
};

// ====================== مركز الإجراءات ======================
const ACTION_TABS = [
  { id: 'expiring', label: 'تنتهي قريباً', tone: 'amber', countKey: 'expiring_count' },
  { id: 'expired', label: 'منتهية', tone: 'red', countKey: 'expired_count' },
  { id: 'idle', label: 'خاملة', tone: 'slate', countKey: 'idle_count' },
];
const TONE_CLS = {
  amber: 'bg-amber-50 text-amber-700 ring-amber-200',
  red: 'bg-red-50 text-red-700 ring-red-200',
  slate: 'bg-slate-100 text-slate-600 ring-slate-200',
};

const ActionCenter = ({ data, onWhatsApp, goTab, delay, onOpenClub }) => {
  const [active, setActive] = useState('expiring');
  const risk = data?.risk || {};
  const rows = risk[active] || [];
  const hint = active === 'expiring' ? 'منشآت ينتهي اشتراكها خلال 14 يوماً — ذكّرها الآن قبل أن تتوقف'
    : active === 'expired' ? 'منشآت انتهى اشتراكها فعلياً — أولوية التجديد'
      : 'منشآت نشطة لم يدخلها أحد منذ 14 يوماً أو أكثر — خطر فقدان العميل';

  return (
    <Panel
      testId="action-center-container" delay={delay}
      title="مركز الإجراءات"
      hint={hint}
      action={
        <div className="flex items-center gap-1.5">
          {ACTION_TABS.map((t) => (
            <button
              key={t.id} onClick={() => setActive(t.id)} data-testid={`action-tab-${t.id}`}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${active === t.id ? `ring-1 ${TONE_CLS[t.tone]}` : 'text-slate-500 hover:bg-slate-50'}`}
            >
              {t.label}
              <span className={`rounded-full px-1.5 text-[10px] tabular-nums ${active === t.id ? 'bg-white/70' : 'bg-slate-100'}`}>{risk[t.countKey] ?? 0}</span>
            </button>
          ))}
          <button onClick={() => goTab('clubs')} className="ms-1 rounded-lg px-2 py-1 text-[11px] font-bold text-teal-700 hover:bg-teal-50" data-testid="action-center-open-clubs">كل المنشآت ←</button>
        </div>
      }
    >
      {rows.length === 0 ? <Empty text="لا يوجد شيء يحتاج تدخلك هنا — ممتاز" /> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
          {rows.map((r) => (
            <div key={r.club_id} className="flex items-center justify-between gap-2 border-b border-slate-100 bg-white px-4 py-3 transition-colors hover:bg-slate-50/70 sm:border-s" data-testid="action-center-row">
              <div className="min-w-0">
                <button type="button" onClick={() => onOpenClub(r.club_id)} className="block max-w-full truncate text-right text-sm font-bold text-slate-800 transition-colors hover:text-teal-700" data-testid="action-center-open-profile">
                  {r.name}
                </button>
                <div className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-400">
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 font-semibold text-slate-500">{planLabel(r.plan)}</span>
                  {active === 'idle'
                    ? <span>{r.idle_days === null ? 'لم يدخل أحد مطلقاً' : `خامل ${r.idle_days} يوم`}</span>
                    : <span>{dateOnly(r.end_date)}</span>}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                {active !== 'idle' && (
                  <span className={`rounded-full px-2 py-1 text-[11px] font-bold tabular-nums ${r.days < 0 ? 'bg-red-50 text-red-600' : r.days <= 7 ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                    {r.days < 0 ? `منتهي منذ ${Math.abs(r.days)} يوم` : `${r.days} يوم`}
                  </span>
                )}
                <button
                  onClick={() => onWhatsApp(r)} disabled={!r.phone} title={r.phone ? 'تذكير واتساب' : 'لا يوجد رقم جوال'}
                  data-testid="action-center-whatsapp-btn"
                  className="rounded-lg p-1.5 text-emerald-600 transition-colors hover:bg-emerald-50 disabled:text-slate-300 disabled:hover:bg-transparent"
                >
                  <Icon name="whatsapp" className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
};

// ====================== الصفحة ======================
export default function CommandCenter({ notify, goTab }) {
  const [cc, setCc] = useState(null);
  const [fin, setFin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [waTarget, setWaTarget] = useState(null);
  const [profileClubId, setProfileClubId] = useState(null);
  const [annModal, setAnnModal] = useState(false);
  const [ann, setAnn] = useState({ title: '', content: '', priority: 'normal', audience: 'all', duration: '7' });
  const [annErr, setAnnErr] = useState(null);

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    const g = (u) => axios.get(`${API}${u}`, sauth()).then((r) => r.data).catch(() => null);
    const [a, b] = await Promise.all([g('/saas/admin/command-center'), g('/saas/admin/financial-overview')]);
    if (a) setCc(a);
    if (b) setFin(b);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => load(true), 60000);
    return () => clearInterval(t);
  }, [load]);

  const sendAnnouncement = async (e) => {
    e.preventDefault(); setAnnErr(null);
    if (!ann.title.trim() || !ann.content.trim()) { setAnnErr('العنوان والمحتوى مطلوبان'); return; }
    try {
      const expires_at = ann.duration === 'none' ? null : new Date(Date.now() + Number(ann.duration) * 86400000).toISOString();
      await axios.post(`${API}/admin/platform-updates`, {
        title: ann.title.trim(), description: ann.content.trim(), badge: 'تعميم', priority: ann.priority,
        audience: ann.audience, status: 'published', is_pinned: ann.priority !== 'normal', expires_at,
      }, sauth());
      setAnnModal(false); setAnn({ title: '', content: '', priority: 'normal', audience: 'all', duration: '7' });
      notify('تم نشر التعميم — سيظهر لدى المستهدفين في التحديثات وأيقونة المساعدة، ويمكنك حذفه أو تعديله في أي وقت من تبويب «تحديثات المنصة»');
    } catch (e2) { setAnnErr(errMsg(e2, 'فشل نشر التعميم')); }
  };

  const c = cc?.counts || {};
  const inbox = cc?.inbox || {};
  const needsAction = (inbox.pending_registrations || 0) + (inbox.pending_club_requests || 0) + (inbox.pending_venue_upgrades || 0) + (inbox.open_tickets || 0) + (inbox.unread_messages || 0);
  const revSeries = (fin?.monthly_revenue || []).slice(-12);
  const hasRevenue = revSeries.some((r) => r.amount > 0);
  const plans = cc?.plans || [];
  const growth = cc?.growth || [];
  const gCur = growth[growth.length - 1] || {};
  const gPrev = growth[growth.length - 2] || {};
  const pct = (cur, prev) => (!prev ? (cur ? 100 : null) : ((cur - prev) / prev) * 100);
  const revCur = revSeries[revSeries.length - 1]?.amount || 0;
  const revPrev = revSeries[revSeries.length - 2]?.amount || 0;

  const copySummary = async () => {
    const lines = [
      '*ملخص منصة تسيار اليوم*',
      '',
      `• الإيراد الشهري المتكرر: ${money(fin?.mrr_estimate)} ريال`,
      `• إيراد هذا الشهر: ${money(fin?.revenue_this_month)} ريال`,
      `• منشآت نشطة: ${nf(c.clubs_active)} من ${nf(c.clubs_total)}`,
      `• متصل الآن: ${nf(c.users_online)} مستخدم`,
      `• تنتهي خلال 14 يوم: ${nf(cc?.risk?.expiring_count)} · منتهية: ${nf(cc?.risk?.expired_count)} · خاملة: ${nf(cc?.risk?.idle_count)}`,
      `• تنتظر إجراءك: ${nf(needsAction)} (${nf(inbox.pending_registrations)} طلب تسجيل · ${nf(inbox.open_tickets)} تذكرة دعم)`,
      '',
      `• حجم المنصة: ${nf(c.players_total)} لاعب · ${nf(c.coaches_total)} مدرب · ${nf(c.teams_total)} فريق · ${nf(c.venues_total)} ملعب`,
      '',
      `محدّث: ${new Date().toLocaleString('ar-SA-u-ca-gregory-nu-latn', { dateStyle: 'medium', timeStyle: 'short' })}`,
    ];
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      notify('تم نسخ ملخص اليوم — الصقه في واتساب أو أي مكان');
    } catch {
      notify('تعذّر النسخ — المتصفح منع الوصول للحافظة');
    }
  };

  if (loading && !cc) {
    return (
      <div className="space-y-4" data-testid="superadmin-overview">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-6">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-28 animate-pulse rounded-2xl bg-slate-200/70" />)}</div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
          <div className="h-80 animate-pulse rounded-2xl bg-slate-200/70 lg:col-span-5" />
          <div className="h-80 animate-pulse rounded-2xl bg-slate-200/70 lg:col-span-7" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="superadmin-overview">
      {/* ترويسة */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold leading-snug text-slate-900">مركز القيادة</h2>
          <p className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-400">
            <PresenceDot online />
            محدّث {relativeTime(cc?.generated_at) || 'الآن'} · يُحدّث تلقائياً كل دقيقة
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={copySummary} className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-600 transition-colors hover:bg-slate-50" data-testid="cc-copy-summary-btn">
            <Icon name="document" className="h-3.5 w-3.5" /> نسخ ملخص اليوم
          </button>
          <button onClick={() => load()} className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-600 transition-colors hover:bg-slate-50" data-testid="cc-refresh-btn">
            <Icon name="refresh" className="h-3.5 w-3.5" /> تحديث
          </button>
          <PrimaryBtn onClick={() => setAnnModal(true)} data-testid="announcement-btn"><Icon name="bell" className="w-4 h-4" /> إرسال تعميم</PrimaryBtn>
        </div>
      </div>

      {/* شريط المؤشرات */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-6" data-testid="cc-kpi-strip">
        <Kpi testId="kpi-card-mrr" tone="dark" label="الإيراد الشهري المتكرر" value={money(fin?.mrr_estimate)} unit="ريال" accent="text-emerald-400"
          sub={`${nf(fin?.paying_clubs_count)} منشأة · ${nf(fin?.paying_coaches_count)} مدرب مدفوع`} delay={0} onClick={() => goTab('accounting')} />
        <Kpi testId="kpi-card-monthly-revenue" label="إيراد هذا الشهر" value={money(fin?.revenue_this_month)} unit="ريال" accent="text-indigo-600"
          sub={`الإجمالي ${money(fin?.total_revenue)} ريال`} icon="wallet" delay={1} trend={pct(revCur, revPrev)} onClick={() => goTab('accounting')} />
        <Kpi testId="kpi-card-expiring-subs" label="تنتهي خلال 14 يوم" value={nf(cc?.risk?.expiring_count)} accent="text-amber-600"
          sub={`${nf(cc?.risk?.expired_count)} منتهية بالفعل`} icon="clock" delay={2} onClick={() => goTab('clubs')} />
        <Kpi testId="kpi-card-tickets-requests" label="تنتظر إجراءك" value={nf(needsAction)} accent={needsAction ? 'text-red-600' : 'text-emerald-600'}
          sub={`${nf(inbox.pending_registrations)} طلب تسجيل · ${nf(inbox.open_tickets)} تذكرة`} icon="inbox" delay={3} onClick={() => goTab('requests')} />
        <Kpi testId="kpi-card-clubs-active" label="منشآت نشطة" value={nf(c.clubs_active)} unit={`/ ${nf(c.clubs_total)}`} accent="text-teal-700"
          sub={`+${nf(gCur.clubs || 0)} هذا الشهر · ${nf(c.clubs_trial)} تجريبي`} icon="building" delay={4} trend={pct(gCur.clubs, gPrev.clubs)} onClick={() => goTab('clubs')} />
        <Kpi testId="kpi-card-users-online" tone="emerald" label="متصل الآن" value={nf(c.users_online)} accent="text-emerald-300" live
          sub={`من ${nf(c.users_active)} مستخدم نشط`} delay={5} onClick={() => goTab('users')} />
      </div>

      {/* الرسوم جنباً إلى جنب */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Panel testId="chart-monthly-revenue" title="اتجاه الإيرادات" hint="آخر 12 شهراً (ريال)" delay={6}>
          <div className="h-56 px-2 pt-4">
            {!hasRevenue ? <Empty text="لا توجد مدفوعات مسجلة بعد — أضف دفعة من تبويب المحاسبة المالية" /> : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revSeries} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                  <defs>
                    <linearGradient id="ccRev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0d9488" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#0d9488" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="month" tickFormatter={monthLabel} tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis orientation="right" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={44} />
                  <Tooltip content={<ChartTip suffix="ريال" />} />
                  <Area type="monotone" dataKey="amount" name="الإيراد" stroke="#0d9488" strokeWidth={2.5} fill="url(#ccRev)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </Panel>
        <Panel testId="chart-clubs-users-growth" title="النمو" hint="منشآت ومستخدمون ولاعبون جدد — آخر 6 أشهر" delay={7}>
          <div className="h-56 px-2 pt-4">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={cc?.growth || []} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="month" tickFormatter={monthLabel} tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="bars" orientation="right" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={30} />
                <YAxis yAxisId="players" orientation="left" tick={{ fontSize: 10, fill: '#0d9488' }} axisLine={false} tickLine={false} width={34} />
                <Tooltip content={<ChartTip />} />
                <Bar yAxisId="bars" dataKey="clubs" name="منشآت" fill="#16233b" radius={[6, 6, 0, 0]} barSize={14} />
                <Bar yAxisId="bars" dataKey="users" name="مستخدمون" fill="#6366f1" radius={[6, 6, 0, 0]} barSize={14} />
                <Line yAxisId="players" type="monotone" dataKey="players" name="لاعبون" stroke="#0d9488" strokeWidth={2.5} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>

      {/* مركز الإجراءات — مستطيل كامل */}
      <ActionCenter data={cc} goTab={goTab} delay={8} onOpenClub={setProfileClubId} onWhatsApp={(r) => setWaTarget({ name: r.name, phone: r.phone, plan_name: planLabel(r.plan), expiry_date: r.end_date, amount: r.amount })} />

      {/* النبض المباشر */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        <Panel testId="live-users-list" className="lg:col-span-4" title="المتصلون الآن" hint={`${nf(c.users_online)} مستخدم داخل النظام`} delay={9}
          action={<button onClick={() => goTab('users')} className="rounded-lg px-2 py-1 text-[11px] font-bold text-teal-700 hover:bg-teal-50" data-testid="live-users-open">المستخدمون ←</button>}>
          <div className="max-h-64 divide-y divide-slate-50 overflow-y-auto">
            {(cc?.online_users || []).length === 0 ? <Empty text="لا أحد متصل الآن" /> : cc.online_users.map((u, i) => (
              <div key={i} className="flex items-center gap-2.5 px-4 py-2.5" data-testid="live-user-row">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-xs font-bold text-emerald-700">{(u.name || '؟').slice(0, 1)}</span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-bold text-slate-800">{u.name}</div>
                  <div className="truncate text-[11px] text-slate-400">{u.club_name} · {ROLE_SHORT[u.role] || u.role}</div>
                </div>
                <PresenceDot online />
              </div>
            ))}
          </div>
        </Panel>

        <Panel testId="activity-feed-stream" className="lg:col-span-4" title="آخر الانضمامات" hint="منشآت ومدربون وملاعب جديدة" delay={10}>
          <div className="max-h-64 overflow-y-auto px-4 py-3">
            {(cc?.feed || []).length === 0 ? <Empty text="لا يوجد نشاط" /> : (
              <ol className="relative space-y-3 border-e border-slate-100 pe-4">
                {cc.feed.map((f, i) => (
                  <li key={i} className="relative" data-testid="activity-feed-row">
                    <span className={`absolute -end-[21px] top-1.5 h-2.5 w-2.5 rounded-full ring-2 ring-white ${f.type === 'club' ? 'bg-teal-500' : f.type === 'coach' ? 'bg-indigo-500' : 'bg-amber-500'}`} />
                    {f.club_id ? (
                      <button type="button" onClick={() => setProfileClubId(f.club_id)} className="block text-right text-sm font-bold text-slate-800 transition-colors hover:text-teal-700" data-testid="activity-feed-open-club">{f.name}</button>
                    ) : (
                      <div className="text-sm font-bold text-slate-800">{f.name}</div>
                    )}
                    <div className="text-[11px] text-slate-400">
                      {f.type === 'club' ? 'منشأة جديدة' : f.type === 'coach' ? 'مدرب جديد' : 'ملعب جديد'} · {f.type === 'club' ? planLabel(f.meta) : f.meta} · {relativeTime(f.at)}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </Panel>

        <Panel testId="chart-plans-distribution-donut" className="lg:col-span-4" title="توزيع الباقات" hint={`${nf(c.clubs_total)} منشأة`} delay={11}>
          <div className="flex items-center gap-2 p-3">
            <div className="h-44 w-1/2">
              {plans.length === 0 ? <Empty text="—" /> : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={plans} dataKey="count" nameKey="plan" innerRadius="58%" outerRadius="88%" paddingAngle={3} stroke="none">
                      {plans.map((p, i) => <Cell key={p.plan} fill={PLAN_COLORS[i % PLAN_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v, n) => [`${v} منشأة`, planLabel(n)]} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
            <ul className="flex-1 space-y-2 pe-1">
              {plans.map((p, i) => (
                <li key={p.plan} className="flex items-center justify-between gap-2 text-xs" data-testid="plan-legend-row">
                  <span className="flex items-center gap-1.5 font-semibold text-slate-600">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: PLAN_COLORS[i % PLAN_COLORS.length] }} />
                    {planLabel(p.plan)}
                  </span>
                  <span className="tabular-nums font-bold text-slate-800">{p.count}</span>
                </li>
              ))}
            </ul>
          </div>
        </Panel>
      </div>

      {/* حجم المنصة */}
      <Panel testId="cc-scale-strip" title="حجم المنصة" hint="إجمالي ما تديره تسيار اليوم" delay={12}>
        <div className="grid grid-cols-3 divide-x divide-x-reverse divide-slate-100 sm:grid-cols-6">
          {[['لاعب', c.players_total, 'users'], ['مستخدم', c.users_total, 'users'], ['مدرب', c.coaches_total, 'whistle'],
            ['فريق', c.teams_total, 'userGroup'], ['ملعب', c.venues_total, 'field'], ['مباراة', c.matches_total, 'flag']].map(([l, v, ic]) => (
              <div key={l} className="px-4 py-3.5" data-testid={`cc-scale-${l}`}>
                <div className="flex items-center gap-1.5 text-[11px] font-bold text-slate-400"><Icon name={ic} className="h-3.5 w-3.5" /> {l}</div>
                <div className="mt-1 text-xl font-extrabold tabular-nums text-slate-900">{nf(v)}</div>
              </div>
            ))}
        </div>
      </Panel>

      <CartatInterestSection />

      <AdminWhatsAppModal open={Boolean(waTarget)} onClose={() => setWaTarget(null)} type="club_subscription" entity={waTarget || {}} />

      <ClubProfileDrawer
        clubId={profileClubId}
        onClose={() => setProfileClubId(null)}
        onChanged={() => load(true)}
        notify={notify}
        onWhatsApp={(e) => { setProfileClubId(null); setWaTarget(e); }}
      />

      <Modal open={annModal} title="نشر تعميم / تحديث جديد" onClose={() => setAnnModal(false)}>
        {annErr && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="announcement-error">{annErr}</div>}
        <form onSubmit={sendAnnouncement} className="space-y-3">
          <Field label="العنوان" required><input className={inputCls} value={ann.title} onChange={(e) => setAnn({ ...ann, title: e.target.value })} data-testid="announcement-title" /></Field>
          <Field label="المحتوى" required><textarea rows={3} className={inputCls} value={ann.content} onChange={(e) => setAnn({ ...ann, content: e.target.value })} data-testid="announcement-content" /></Field>
          <Field label="يظهر لـ">
            <select className={inputCls} value={ann.audience} onChange={(e) => setAnn({ ...ann, audience: e.target.value })} data-testid="announcement-audience">
              <option value="all">جميع المنشآت والمدربين</option>
              <option value="clubs">الأندية فقط</option>
              <option value="academies">الأكاديميات فقط</option>
              <option value="coaches">المدربون فقط</option>
            </select>
          </Field>
          <Field label="الأولوية">
            <select className={inputCls} value={ann.priority} onChange={(e) => setAnn({ ...ann, priority: e.target.value })} data-testid="announcement-priority">
              <option value="normal">عادية</option>
              <option value="high">مرتفعة (بطاقة تنبيه فورية)</option>
              <option value="urgent">عاجلة (بطاقة تنبيه فورية)</option>
            </select>
          </Field>
          <Field label="مدة ظهور التعميم">
            <select className={inputCls} value={ann.duration} onChange={(e) => setAnn({ ...ann, duration: e.target.value })} data-testid="announcement-duration">
              <option value="3">3 أيام</option>
              <option value="7">7 أيام</option>
              <option value="14">14 يوم</option>
              <option value="30">30 يوم</option>
              <option value="none">بدون تحديد (يبقى حتى تحذفه يدوياً)</option>
            </select>
            <p className="mt-1 text-xs text-slate-400">بعد انتهاء المدة سيختفي التعميم تلقائياً من جميع المنشآت، القديمة والجديدة.</p>
          </Field>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setAnnModal(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" data-testid="announcement-submit">نشر</PrimaryBtn>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function CartatInterestSection() {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/saas/admin/cartat-interest`, sauth()).then((r) => setData(r.data)).catch(() => setData({ total: 0, items: [] }));
  }, []);

  return (
    <Panel
      testId="cartat-interest-section" delay={13}
      title="اهتمام المنشآت — واتساب للأعمال عبر كارتات"
      action={<span className="rounded-full bg-orange-50 px-3 py-1 text-xs font-bold text-orange-700" data-testid="cartat-interest-count">{data?.total ?? '—'} منشأة مهتمة</span>}
    >
      <div className="divide-y divide-slate-50">
        {!data ? <Empty text="جارٍ التحميل..." />
          : data.items.length === 0 ? <Empty text="لا توجد منشآت مهتمة بعد" />
            : data.items.map((it, i) => (
              <div key={i} className="flex items-center justify-between px-4 py-2.5 text-sm" data-testid="cartat-interest-row">
                <span className="font-semibold text-slate-800">{it.club_name}</span>
                <span className="text-xs text-slate-400">{it.user_name} · {relativeTime(it.created_at)}</span>
              </div>
            ))}
      </div>
    </Panel>
  );
}
